import { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import type { Dispatch } from 'redux';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ISelectOption, SelectInput } from 'UIPackage/MainModule/components/SelectInput';
import {
  getKnoOrganizationWithChildrenSelectOptions,
  getUserKnoSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { catalogKnoOrganizationWithChildrenGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { KnmAttachments } from 'KnmPackage/KnmDocumentsModule/components/KnmAttachments';
import { PASS_SIGNATURE_VALIDATION, INVALID_DOCUMENT_SIGNATURE } from 'CorePackage/MainModule/constants/notices';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { convertFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertFileToBase64';
import { convertSignFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertSignFileToBase64';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';

import styles from './PlanChangeKnoModal.module.scss';
import { planChangeKno } from '../actions/planActions';
import {
  getPlanDataKnoOrganizationDictVersionId,
  getPlanDataSupervisionDictVersionId,
} from '../selectors/planSelectors';

interface IPlanChangeKnoModalPropsWithoutStore {
  isVisible: boolean;
  onClose: () => void;
}

interface IPlanChangeKnoModalStoreProps {
  knoSelectOptions: IUserOrganisationSelectOption[] | undefined;
  knoWithChildrenSelectOptions: IUserOrganisationSelectOption[] | undefined;
  knoDictVersionId: string | undefined;
  supervisionTypeDictVersionId: string | undefined;
}

const PlanChangeKnoModalWithoutStore = ({
  isVisible,
  onClose,
  knoSelectOptions,
  knoWithChildrenSelectOptions,
  knoDictVersionId,
  supervisionTypeDictVersionId,
}: IPlanChangeKnoModalStoreProps & IPlanChangeKnoModalPropsWithoutStore) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [knoValue, setKnoValue] = useState<ISelectOption>();
  const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);
  const { uploadDocumentSignatures, uploadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const [signs, setSigns] = useState<Array<File>>([]);
  const [document, setDocument] = useState<File | null>(null);
  const { id: planId } = useParams<{ id: string }>();

  useEffect(() => {
    dispatch(catalogKnoOrganizationWithChildrenGet());
  }, [knoDictVersionId, supervisionTypeDictVersionId]);

  const handleChangeKno = useCallback((value: ISelectOption) => setKnoValue(value), []);

  const handleRemoveDocument = useCallback(() => {
    setDocument(null);
  }, []);

  const handleRemoveSign = useCallback(
    (index: number) => () => {
      setSigns(prev => prev.filter((el: File, idx: number) => idx !== index));
    },
    [],
  );

  const handleSetAttachment = useCallback((file: File, isSign: boolean) => {
    isSign ? setSigns(prev => [...prev, file]) : setDocument(file);
  }, []);

  const handleSaveModalButton = useCallback(async () => {
    if (knoValue && document) {
      if (document && signs.length) {
        const convertedDocument = await convertFileToBase64(document);
        const signActions = signs.map(async sign => {
          const signText = await convertSignFileToBase64(sign);

          return signText;
        });
        const signTextArr = await Promise.all(signActions);
        const files = {
          document: convertedDocument,
          signatures: signTextArr,
        };
        const res = await uploadDocumentSignatures(files);

        if (res && res.signed) {
          const attachment = await uploadKnmDocumentAttachment(document);

          dispatch(
            addNoticeAction({
              renderContent: PASS_SIGNATURE_VALIDATION,
              noticeType: NoticeTypesEnum.SUCCESS,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
            }),
          );
          if (res.signatureInfo && attachment) {
            const signedDocument: IKnmAttachment = {
              guid: attachment.guid || null,
              dateCreate: attachment.dateCreate || null,
              fileName: attachment.fileName || null,
              filePath: attachment.filePath || null,
              fileSize: attachment.fileSize,
              mimeType: attachment.mimeType || null,
              bucketName: attachment.bucketName || null,
              correlationToken: attachment.correlationToken || null,
              hash: attachment.hash || null,
              signed: true,
              signatureInfo: res.signatureInfo,
            };

            dispatch(planChangeKno(+planId, knoValue, signedDocument));
            setIsConfirmationModalVisible(false);
            onClose();
          }
        } else {
          dispatch(
            addNoticeAction({
              renderContent: INVALID_DOCUMENT_SIGNATURE,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
            }),
          );
        }
      } else if (document) {
        const attachments = await uploadKnmDocumentAttachment(document);

        attachments && dispatch(planChangeKno(+planId, knoValue, attachments));
        setIsConfirmationModalVisible(false);
        onClose();
      }
    }
  }, [dispatch, document, knoValue, onClose, planId, signs, uploadDocumentSignatures, uploadKnmDocumentAttachment]);

  const handleOpenConfirmationModal = useCallback(() => setIsConfirmationModalVisible(true), []);
  const handleCloseConfirmationModal = useCallback(() => setIsConfirmationModalVisible(false), []);

  const knoOrganizationSelectOptions =
    knoDictVersionId && supervisionTypeDictVersionId ? knoWithChildrenSelectOptions : knoSelectOptions;

  return (
    <Modal modalBodyClassName={styles.ModalBody} visible={isVisible} onClose={onClose}>
      <ModalHeader title="Изменение КНО в плане и проверках плана" sizeText="small" color="black" />
      <ModalBody size="content" border="bottom">
        <div className={styles.InputBlock}>
          <div className={styles.InputContainer}>
            <SelectInput
              name="knoName"
              options={knoOrganizationSelectOptions}
              value={knoValue}
              onChange={handleChangeKno}
              placeholder="Выберите новый КНО"
            />
            <KnmAttachments
              document={document}
              signs={signs}
              handleRemoveDocument={handleRemoveDocument}
              handleRemoveSign={handleRemoveSign}
              handleSetAttachment={handleSetAttachment}
            />
          </div>
        </div>
      </ModalBody>
      <ModalActions>
        <Button text="Отменить" size="medium" onClick={onClose} />
        <Button
          text="Изменить"
          disabled={!knoValue || !document || !signs.length}
          size="medium"
          onClick={handleOpenConfirmationModal}
        />
      </ModalActions>
      <Modal
        modalBodyClassName={styles.AdditionalModalBody}
        visible={isConfirmationModalVisible}
        onClose={handleCloseConfirmationModal}
      >
        <ModalHeader title="Подтверждение операции" sizeText="small" color="black" />
        <ModalBody size="content" border="bottom">
          Данная операция изменит КНО во всех незавершенных КНМ плана. По завершении операции вам необходимо будет
          подписать все КНМ плана
        </ModalBody>
        <ModalActions>
          <Button text="Отмена" size="medium" onClick={handleCloseConfirmationModal} />
          <Button text="Да" disabled={!knoValue} size="medium" onClick={handleSaveModalButton} />
        </ModalActions>
      </Modal>
    </Modal>
  );
};

const mapStateToProps = (state: IRootState): IPlanChangeKnoModalStoreProps => ({
  knoSelectOptions: getUserKnoSelectOptions(state),
  knoWithChildrenSelectOptions: getKnoOrganizationWithChildrenSelectOptions(state),
  knoDictVersionId: getPlanDataKnoOrganizationDictVersionId(state),
  supervisionTypeDictVersionId: getPlanDataSupervisionDictVersionId(state),
});

export const PlanChangeKnoModal = connect(mapStateToProps)(PlanChangeKnoModalWithoutStore);
